import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { useAuth } from "@/auth/AuthProvider";
import { api } from "@/lib/api";

type Supplier = { id: number; name: string };

type InvoiceFile = {
  id: number;
  original_name: string;
  mime: string | null;
  size_bytes: number | null;
  sha256: string | null;
  uploaded_at: string;
};

type InvoiceItem = {
  id?: number;
  product_id: number | null;
  product_name_snapshot: string;
  unit: string | null;
  qty: string;
  unit_price: string;
  amount: string;
  note: string | null;
};

type Invoice = {
  id: number;
  invoice_no: string;
  invoice_date: string;
  supplier_id: number;
  category: string | null;
  amount_before_tax: string;
  vat_rate: string;
  vat_amount: string;
  total: string;
  note: string | null;
  affects_stock: boolean;
};

type InvoiceDetail = Invoice & {
  items: InvoiceItem[];
  files: InvoiceFile[];
  supplier_name: string | null;
};

const today = () => new Date().toISOString().slice(0, 10);
const startOfYear = () => today().slice(0, 4) + "-01-01";

const fmtVND = (v: string | number | null) =>
  v === null || v === undefined
    ? "—"
    : Number(v).toLocaleString("vi-VN", { maximumFractionDigits: 0 }) + "đ";

const fmtDate = (s: string | null) =>
  s ? new Date(s + (s.length === 10 ? "T00:00:00" : "")).toLocaleDateString("vi-VN") : "—";

export function InputInvoicesPage() {
  const { user } = useAuth();
  const canWrite = user?.role === "admin" || user?.role === "ketoan";
  const qc = useQueryClient();
  // Default: từ đầu năm để khỏi che hoá đơn cũ — user có thể thu hẹp lại bất kỳ lúc nào.
  const [dateFrom, setDateFrom] = useState(startOfYear());
  const [dateTo, setDateTo] = useState(today());
  const [supplierFilter, setSupplierFilter] = useState<number | "">("");
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [search, setSearch] = useState("");
  const [openedId, setOpenedId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);

  const suppliersQ = useQuery({
    queryKey: ["suppliers", "for-invoice"],
    queryFn: async () =>
      (await api.get<Supplier[]>("/suppliers", { params: { active: true } })).data,
  });

  // listQ trả về danh sách CHƯA filter theo category (để dropdown Loại có đầy đủ options).
  // Category filter áp dụng client-side bên dưới.
  const listQ = useQuery({
    queryKey: ["input-invoices", dateFrom, dateTo, supplierFilter, search],
    queryFn: async () => {
      const params: any = { date_from: dateFrom, date_to: dateTo };
      if (supplierFilter) params.supplier_id = supplierFilter;
      if (search) params.q = search;
      return (await api.get<Invoice[]>("/input-invoices", { params })).data;
    },
  });

  // Danh sách hiển thị = listQ.data lọc theo categoryFilter (client-side).
  const displayRows = categoryFilter
    ? (listQ.data ?? []).filter((r) => (r.category ?? "") === categoryFilter)
    : (listQ.data ?? []);

  const totals = displayRows.reduce(
    (acc, i) => {
      acc.total += Number(i.total) || 0;
      acc.vat += Number(i.vat_amount) || 0;
      acc.before += Number(i.amount_before_tax) || 0;
      return acc;
    },
    { total: 0, vat: 0, before: 0 }
  );

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">
            Hoá đơn đầu vào
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Chi phí mua vật tư / dịch vụ / quảng cáo.
          </p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <a
              href="/dong-bo-drive"
              className="btn bg-emerald-600 text-white hover:bg-emerald-700"
            >
              📁 Sync Drive
            </a>
            <button
              onClick={() => setUploadOpen(true)}
              className="btn bg-purple-600 text-white hover:bg-purple-700"
            >
              📄 Upload PDF
            </button>
            <button
              onClick={() => setCreating(true)}
              className="btn-primary"
            >
              + Tạo HĐ
            </button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-2 mb-3">
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Từ</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Đến</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="border rounded px-2 py-1 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Nhà cung cấp</label>
          <select
            value={supplierFilter}
            onChange={(e) =>
              setSupplierFilter(e.target.value ? Number(e.target.value) : "")
            }
            className="border rounded px-2 py-1 text-sm min-w-[12rem]"
          >
            <option value="">— Tất cả —</option>
            {suppliersQ.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-0.5">Loại</label>
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="border rounded px-2 py-1 text-sm min-w-[10rem]"
          >
            <option value="">— Tất cả —</option>
            {/* Lấy danh sách Loại từ dữ liệu thực, sort A-Z, ẩn rỗng */}
            {Array.from(
              new Set(
                (listQ.data ?? [])
                  .map((r) => r.category)
                  .filter((c): c is string => !!c && c.trim() !== "")
              )
            )
              .sort()
              .map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
          </select>
        </div>
        <div className="flex-1 min-w-[12rem]">
          <label className="block text-xs text-gray-500 mb-0.5">Tìm</label>
          <input
            placeholder="Số HĐ / ghi chú..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full border rounded px-2 py-1 text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mb-3">
        <Stat
          label="Số hoá đơn"
          value={
            displayRows.length.toString() +
            (categoryFilter
              ? ` / ${listQ.data?.length ?? 0}`
              : "")
          }
        />
        <Stat label="Tổng tiền chưa VAT" value={fmtVND(totals.before)} />
        <Stat label="Tổng tiền có VAT" value={fmtVND(totals.total)} />
      </div>

      <div className="bg-white border rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-3 py-2">Số HĐ</th>
              <th className="text-left px-3 py-2">Ngày</th>
              <th className="text-left px-3 py-2">Nhà cung cấp</th>
              <th className="text-left px-3 py-2">Loại</th>
              <th className="text-right px-3 py-2">Trước VAT</th>
              <th className="text-right px-3 py-2">VAT</th>
              <th className="text-right px-3 py-2">Tổng</th>
              <th className="text-center px-3 py-2">Kho</th>
            </tr>
          </thead>
          <tbody>
            {listQ.isLoading && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                  Đang tải...
                </td>
              </tr>
            )}
            {!listQ.isLoading && displayRows.length === 0 && (
              <tr>
                <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                  {categoryFilter
                    ? `Không có hoá đơn loại "${categoryFilter}" trong khoảng này.`
                    : "Chưa có hoá đơn trong khoảng này."}
                </td>
              </tr>
            )}
            {displayRows.map((i) => {
              const supplierName =
                suppliersQ.data?.find((s) => s.id === i.supplier_id)?.name ?? `#${i.supplier_id}`;
              return (
                <tr
                  key={i.id}
                  className="border-t hover:bg-gray-50 cursor-pointer"
                  onClick={() => setOpenedId(i.id)}
                >
                  <td className="px-3 py-2 font-mono">{i.invoice_no}</td>
                  <td className="px-3 py-2">{fmtDate(i.invoice_date)}</td>
                  <td className="px-3 py-2">{supplierName}</td>
                  <td className="px-3 py-2 text-xs text-gray-600">{i.category ?? "—"}</td>
                  <td className="px-3 py-2 text-right">{fmtVND(i.amount_before_tax)}</td>
                  <td className="px-3 py-2 text-right text-xs text-gray-600">
                    {fmtVND(i.vat_amount)}
                  </td>
                  <td className="px-3 py-2 text-right font-medium">{fmtVND(i.total)}</td>
                  <td className="px-3 py-2 text-center">{i.affects_stock ? "✓" : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {creating && (
        <InvoiceFormDrawer
          suppliers={suppliersQ.data ?? []}
          onClose={() => setCreating(false)}
          onSaved={(id) => {
            qc.invalidateQueries({ queryKey: ["input-invoices"] });
            setCreating(false);
            setOpenedId(id);
          }}
        />
      )}

      {openedId !== null && (
        <DetailDrawer
          id={openedId}
          onClose={() => setOpenedId(null)}
          canWrite={canWrite}
          onChange={() => qc.invalidateQueries({ queryKey: ["input-invoices"] })}
        />
      )}

      {uploadOpen && (
        <UploadPdfDrawer
          onClose={() => setUploadOpen(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["input-invoices"] });
            setUploadOpen(false);
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white border rounded p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-lg font-medium">{value}</div>
    </div>
  );
}

function emptyItem(): InvoiceItem {
  return {
    product_id: null,
    product_name_snapshot: "",
    unit: "",
    qty: "1",
    unit_price: "0",
    amount: "0",
    note: null,
  };
}

function InvoiceFormDrawer({
  suppliers,
  onClose,
  onSaved,
}: {
  suppliers: Supplier[];
  onClose: () => void;
  onSaved: (id: number) => void;
}) {
  const [form, setForm] = useState({
    invoice_no: "",
    invoice_date: today(),
    supplier_id: suppliers[0]?.id ?? 0,
    category: "hàng hoá",
    amount_before_tax: "0",
    vat_rate: "0.08",
    vat_amount: "0",
    total: "0",
    note: "",
    affects_stock: false,
    items: [emptyItem()] as InvoiceItem[],
  });

  // auto-recalc VAT + total when amount_before_tax or vat_rate changes
  useEffect(() => {
    const before = Number(form.amount_before_tax) || 0;
    const rate = Number(form.vat_rate) || 0;
    const vat = Math.round(before * rate);
    const total = before + vat;
    setForm((f) => ({
      ...f,
      vat_amount: String(vat),
      total: String(total),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.amount_before_tax, form.vat_rate]);

  // auto-update amount_before_tax = sum(items.amount)
  useEffect(() => {
    const sum = form.items.reduce((a, it) => a + (Number(it.amount) || 0), 0);
    setForm((f) => (Number(f.amount_before_tax) !== sum ? { ...f, amount_before_tax: String(sum) } : f));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.items]);

  const save = useMutation({
    mutationFn: async () => {
      const r = await api.post<InvoiceDetail>("/input-invoices", {
        ...form,
        amount_before_tax: Number(form.amount_before_tax),
        vat_rate: Number(form.vat_rate),
        vat_amount: Number(form.vat_amount),
        total: Number(form.total),
        items: form.items.map((it) => ({
          ...it,
          qty: Number(it.qty),
          unit_price: Number(it.unit_price),
          amount: Number(it.amount),
        })),
      });
      return r.data;
    },
    onSuccess: (data) => onSaved(data.id),
  });

  const updateItem = (idx: number, patch: Partial<InvoiceItem>) => {
    setForm((f) => {
      const items = [...f.items];
      items[idx] = { ...items[idx], ...patch };
      // auto compute amount = qty * unit_price when those change
      if ("qty" in patch || "unit_price" in patch) {
        items[idx].amount = String(
          Math.round((Number(items[idx].qty) || 0) * (Number(items[idx].unit_price) || 0))
        );
      }
      return { ...f, items };
    });
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-end z-50">
      <div className="w-full max-w-3xl bg-white h-full overflow-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Tạo hoá đơn đầu vào</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">
            ✕
          </button>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          className="space-y-3"
        >
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Số HĐ *</label>
              <input
                value={form.invoice_no}
                onChange={(e) => setForm({ ...form, invoice_no: e.target.value })}
                required
                className="w-full border rounded px-2 py-1.5 text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Ngày HĐ *</label>
              <input
                type="date"
                value={form.invoice_date}
                onChange={(e) => setForm({ ...form, invoice_date: e.target.value })}
                required
                className="w-full border rounded px-2 py-1.5 text-sm"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Nhà cung cấp *</label>
              <select
                value={form.supplier_id}
                onChange={(e) => setForm({ ...form, supplier_id: Number(e.target.value) })}
                required
                className="w-full border rounded px-2 py-1.5 text-sm"
              >
                {suppliers.length === 0 && <option value="">— Chưa có —</option>}
                {suppliers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Loại</label>
              <select
                value={form.category ?? ""}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm"
              >
                <option value="hàng hoá">Hàng hoá</option>
                <option value="nguyên liệu">Nguyên liệu</option>
                <option value="dịch vụ">Dịch vụ</option>
                <option value="CCDC">Công cụ dụng cụ</option>
                <option value="khác">Khác</option>
              </select>
            </div>
          </div>

          <div className="border rounded">
            <div className="bg-gray-50 px-3 py-2 text-sm font-medium flex items-center justify-between">
              <span>Mặt hàng</span>
              <button
                type="button"
                onClick={() => setForm({ ...form, items: [...form.items, emptyItem()] })}
                className="text-xs text-blue-600 hover:underline"
              >
                + Thêm dòng
              </button>
            </div>
            <table className="w-full text-xs">
              <thead className="text-gray-600">
                <tr>
                  <th className="text-left px-2 py-1">Tên hàng</th>
                  <th className="text-left px-2 py-1 w-16">ĐVT</th>
                  <th className="text-right px-2 py-1 w-16">SL</th>
                  <th className="text-right px-2 py-1 w-28">Đơn giá</th>
                  <th className="text-right px-2 py-1 w-32">Thành tiền</th>
                  <th className="w-6"></th>
                </tr>
              </thead>
              <tbody>
                {form.items.map((it, idx) => (
                  <tr key={idx} className="border-t">
                    <td className="px-2 py-1">
                      <input
                        value={it.product_name_snapshot}
                        onChange={(e) => updateItem(idx, { product_name_snapshot: e.target.value })}
                        required
                        className="w-full border rounded px-1 py-0.5"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        value={it.unit ?? ""}
                        onChange={(e) => updateItem(idx, { unit: e.target.value })}
                        className="w-full border rounded px-1 py-0.5"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="number"
                        step="0.001"
                        value={it.qty}
                        onChange={(e) => updateItem(idx, { qty: e.target.value })}
                        className="w-full border rounded px-1 py-0.5 text-right"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        type="number"
                        value={it.unit_price}
                        onChange={(e) => updateItem(idx, { unit_price: e.target.value })}
                        className="w-full border rounded px-1 py-0.5 text-right"
                      />
                    </td>
                    <td className="px-2 py-1 text-right font-medium">{fmtVND(it.amount)}</td>
                    <td className="px-2 py-1">
                      {form.items.length > 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })
                          }
                          className="text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid grid-cols-4 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Trước VAT</label>
              <input
                value={form.amount_before_tax}
                readOnly
                className="w-full border rounded px-2 py-1.5 text-sm text-right bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Tỷ lệ VAT</label>
              <select
                value={form.vat_rate}
                onChange={(e) => setForm({ ...form, vat_rate: e.target.value })}
                className="w-full border rounded px-2 py-1.5 text-sm"
              >
                <option value="0">0%</option>
                <option value="0.05">5%</option>
                <option value="0.08">8%</option>
                <option value="0.10">10%</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Tiền VAT</label>
              <input
                value={form.vat_amount}
                readOnly
                className="w-full border rounded px-2 py-1.5 text-sm text-right bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-0.5">Tổng tiền</label>
              <input
                value={form.total}
                readOnly
                className="w-full border rounded px-2 py-1.5 text-sm text-right bg-gray-50 font-medium"
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.affects_stock}
                onChange={(e) => setForm({ ...form, affects_stock: e.target.checked })}
              />
              Ảnh hưởng đến tồn kho
            </label>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-0.5">Ghi chú</label>
            <textarea
              value={form.note ?? ""}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              rows={2}
              className="w-full border rounded px-2 py-1.5 text-sm"
            />
          </div>

          {save.error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm">
              {(save.error as any)?.response?.data?.detail ?? String(save.error)}
            </div>
          )}

          <div className="text-xs text-gray-500">
            Tạo xong sẽ mở chi tiết để đính kèm PDF/ảnh.
          </div>

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
            >
              Huỷ
            </button>
            <button
              type="submit"
              disabled={save.isPending}
              className="flex-1 px-3 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-60"
            >
              {save.isPending ? "Đang lưu..." : "Tạo hoá đơn"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DetailDrawer({
  id,
  onClose,
  canWrite,
  onChange,
}: {
  id: number;
  onClose: () => void;
  canWrite: boolean;
  onChange: () => void;
}) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const detailQ = useQuery({
    queryKey: ["invoice", id],
    queryFn: async () => (await api.get<InvoiceDetail>(`/input-invoices/${id}`)).data,
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/input-invoices/${id}/files`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["invoice", id] });
      if (fileRef.current) fileRef.current.value = "";
    },
  });

  const deleteFile = useMutation({
    mutationFn: async (fileId: number) => {
      await api.delete(`/input-invoices/${id}/files/${fileId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invoice", id] }),
  });

  const deleteInvoice = useMutation({
    mutationFn: async () => {
      await api.delete(`/input-invoices/${id}`);
    },
    onSuccess: () => {
      onChange();
      onClose();
    },
  });

  const openFile = async (fileId: number, name: string, inline: boolean) => {
    const r = await api.get(`/input-invoices/${id}/files/${fileId}/download`, {
      responseType: "blob",
    });
    const ct = r.headers["content-type"];
    const blob = new Blob([r.data], {
      type: typeof ct === "string" ? ct : "application/octet-stream",
    });
    const url = URL.createObjectURL(blob);
    if (inline) {
      window.open(url, "_blank");
    } else {
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-end z-50">
      <div className="w-full max-w-3xl bg-white h-full overflow-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold">
              Hoá đơn <span className="font-mono">{detailQ.data?.invoice_no}</span>
            </h2>
            <div className="text-sm text-gray-500">
              {detailQ.data?.supplier_name} • {fmtDate(detailQ.data?.invoice_date ?? null)}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">
            ✕
          </button>
        </div>

        {detailQ.data && (
          <>
            <div className="grid grid-cols-3 gap-2 mb-4">
              <Stat label="Trước VAT" value={fmtVND(detailQ.data.amount_before_tax)} />
              <Stat
                label={`VAT (${(Number(detailQ.data.vat_rate) * 100).toFixed(0)}%)`}
                value={fmtVND(detailQ.data.vat_amount)}
              />
              <Stat label="Tổng tiền" value={fmtVND(detailQ.data.total)} />
            </div>

            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-1">
                Mặt hàng ({detailQ.data.items.length})
              </div>
              <table className="w-full text-xs border">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-2 py-1">Tên</th>
                    <th className="text-left px-2 py-1">ĐVT</th>
                    <th className="text-right px-2 py-1">SL</th>
                    <th className="text-right px-2 py-1">Đơn giá</th>
                    <th className="text-right px-2 py-1">Thành tiền</th>
                  </tr>
                </thead>
                <tbody>
                  {detailQ.data.items.map((it) => (
                    <tr key={it.id} className="border-t">
                      <td className="px-2 py-1">{it.product_name_snapshot}</td>
                      <td className="px-2 py-1">{it.unit ?? "—"}</td>
                      <td className="px-2 py-1 text-right">{it.qty}</td>
                      <td className="px-2 py-1 text-right">{fmtVND(it.unit_price)}</td>
                      <td className="px-2 py-1 text-right font-medium">
                        {fmtVND(it.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mb-4">
              <div className="text-sm font-medium text-gray-700 mb-1">
                Tệp đính kèm ({detailQ.data.files.length})
              </div>
              <div className="space-y-1">
                {detailQ.data.files.map((f) => (
                  <div
                    key={f.id}
                    className="flex items-center justify-between bg-gray-50 border rounded px-3 py-1.5 text-sm"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-gray-400">📎</span>
                      <button
                        type="button"
                        onClick={() => openFile(f.id, f.original_name, true)}
                        className="text-blue-600 hover:underline truncate text-left"
                      >
                        {f.original_name}
                      </button>
                      <span className="text-xs text-gray-500">
                        {f.size_bytes ? `(${Math.round((f.size_bytes / 1024) * 10) / 10} KB)` : ""}
                      </span>
                      <button
                        type="button"
                        onClick={() => openFile(f.id, f.original_name, false)}
                        className="text-xs text-gray-500 hover:text-gray-900"
                        title="Tải về"
                      >
                        ⬇
                      </button>
                    </div>
                    {canWrite && (
                      <button
                        onClick={() => {
                          if (confirm(`Xoá file "${f.original_name}"?`)) deleteFile.mutate(f.id);
                        }}
                        className="text-red-500 hover:text-red-700 text-xs"
                      >
                        Xoá
                      </button>
                    )}
                  </div>
                ))}
                {detailQ.data.files.length === 0 && (
                  <div className="text-sm text-gray-500 italic px-1">Chưa có tệp.</div>
                )}
              </div>
              {canWrite && (
                <div className="mt-2 flex items-center gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="application/pdf,image/*"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) upload.mutate(f);
                    }}
                    className="text-xs"
                  />
                  {upload.isPending && (
                    <span className="text-xs text-gray-500">Đang upload...</span>
                  )}
                  {upload.error && (
                    <span className="text-xs text-red-600">
                      {(upload.error as any)?.response?.data?.detail ?? "Lỗi upload"}
                    </span>
                  )}
                </div>
              )}
              <div className="text-xs text-gray-500 mt-1">
                Chấp nhận PDF hoặc ảnh, tối đa 25MB/file.
              </div>
            </div>

            {detailQ.data.note && (
              <div className="mb-4">
                <div className="text-sm font-medium text-gray-700 mb-1">Ghi chú</div>
                <div className="text-sm text-gray-700 whitespace-pre-wrap">
                  {detailQ.data.note}
                </div>
              </div>
            )}

            {canWrite && (
              <div className="pt-3 border-t">
                <button
                  onClick={() => {
                    if (
                      confirm(
                        `Xoá hoá đơn ${detailQ.data?.invoice_no}? Tất cả tệp đính kèm sẽ bị xoá theo.`
                      )
                    )
                      deleteInvoice.mutate();
                  }}
                  className="px-3 py-1.5 border border-red-200 text-red-700 rounded text-sm hover:bg-red-50"
                >
                  Xoá hoá đơn
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

type ParsedInvoicePreview = {
  filename: string;
  filesize: number;
  success: boolean;
  error: string | null;
  parser_used: string;
  seller_tax_code: string | null;
  seller_name: string | null;
  seller_address: string | null;
  invoice_no: string | null;
  invoice_serial: string | null;
  invoice_date: string | null;
  amount_before_tax: number;
  vat_rate: number;
  vat_amount: number;
  total: number;
  category: string | null;
  folder_hint: string | null;
  affects_stock_default: boolean;
  has_vat: boolean;
  items: {
    description: string;
    unit: string | null;
    quantity: number;
    unit_price: number;
    amount: number;
  }[];
  supplier_match: { id: number; name: string } | null;
  duplicate_of: { id: number; date: string | null; total: number } | null;
};

function UploadPdfDrawer({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState<ParsedInvoicePreview[]>([]);
  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    // Chọn folder qua webkitdirectory sẽ trả mọi file con (kể cả non-PDF).
    // Lọc còn PDF + dedup theo name+size để tránh add trùng nhiều lần.
    const pdfs = Array.from(fileList).filter(
      (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")
    );
    setFiles((prev) => {
      const key = (f: File) => `${f.name}::${f.size}`;
      const seen = new Set(prev.map(key));
      const fresh = pdfs.filter((f) => !seen.has(key(f)));
      return [...prev, ...fresh];
    });
  };

  // Trả về TẤT CẢ folder parents (bỏ root + filename) để backend tự chọn match.
  // Browser path dạng "HOÁ ĐƠN/ĐỆM NGỒI/SOITUYET/04062026_500/abc.pdf"
  // → trả ["ĐỆM NGỒI", "SOITUYET", "04062026_500"]
  // Backend duyệt từng phần, ưu tiên match keyword → fallback folder cấp gần root.
  const folderHintOf = (f: File): string[] => {
    const rel = (f as any).webkitRelativePath as string | undefined;
    if (!rel) return [];
    const parts = rel.split("/").filter(Boolean);
    if (parts.length <= 1) return [];
    // Drop chỉ filename — gửi TẤT CẢ parent folders. Backend tự chọn keyword match,
    // skip 'HOÁ ĐƠN' và các root chung.
    return parts.slice(0, -1);
  };

  const parseAll = async () => {
    setParsing(true);
    setErrorMsg(null);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const hints = files.map(folderHintOf);
      fd.append("folder_hints", JSON.stringify(hints));
      const r = await api.post<{ results: ParsedInvoicePreview[] }>(
        "/input-invoices/parse-pdf",
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setParsed(r.data.results);
    } catch (e: any) {
      setErrorMsg(e?.response?.data?.detail ?? String(e));
    } finally {
      setParsing(false);
    }
  };

  // Helper: 1 dòng có sẵn sàng lưu? Skip nếu parse fail / thiếu số HĐ-ngày / trùng /
  // thiếu cả tên NCC + supplier match (không đủ tạo NCC mới) / total = 0.
  const isSavable = (p: ParsedInvoicePreview) =>
    p.success &&
    !!p.invoice_no &&
    !!p.invoice_date &&
    !p.duplicate_of &&
    (!!p.supplier_match || !!(p.seller_name && p.seller_name.trim())) &&
    p.total > 0;

  const saveAll = async () => {
    const valid = parsed.filter(isSavable);
    const skipped = parsed.length - valid.length;
    if (valid.length === 0) {
      setErrorMsg(
        `Không có hoá đơn hợp lệ để lưu (đã loại ${skipped} dòng: parse fail / thiếu số HĐ / trùng / thiếu tên NCC).`
      );
      return;
    }
    setSaving(true);
    setErrorMsg(null);
    try {
      const fd = new FormData();
      const invoicesPayload = valid.map((p) => ({
        invoice_no: p.invoice_no,
        invoice_date: p.invoice_date,
        seller_tax_code: p.seller_tax_code,
        seller_name: p.seller_name,
        seller_address: p.seller_address,
        supplier_id: p.supplier_match?.id ?? null,
        category: p.category,
        amount_before_tax: p.amount_before_tax,
        vat_rate: p.vat_rate,
        vat_amount: p.vat_amount,
        total: p.total,
        items: p.items,
        attached_filename: p.filename,
        affects_stock: p.affects_stock_default,
      }));
      fd.append("invoices_json", JSON.stringify(invoicesPayload));
      files.forEach((f) => fd.append("files", f));
      const r = await api.post<{
        created: any[];
        errors: { invoice_no?: string; error: string }[];
        n_created: number;
        n_errors: number;
      }>("/input-invoices/bulk-create", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const summary = `Đã lưu ${r.data.n_created} hoá đơn${
        skipped > 0 ? ` • Bỏ qua ${skipped} (trùng / thiếu NCC / lỗi parse)` : ""
      }`;
      if (r.data.n_errors > 0) {
        const errLines = r.data.errors
          .map((e) => `• ${e.invoice_no || "?"}: ${e.error}`)
          .join("\n");
        setErrorMsg(
          `${summary}\n${r.data.n_errors} dòng backend báo lỗi:\n${errLines}`
        );
      } else {
        setErrorMsg(null);
        if (skipped > 0) {
          // Có skip nhưng không có error backend → vẫn báo cho user biết
          alert(summary);
        }
        onSaved();
      }
    } catch (e: any) {
      setErrorMsg(e?.response?.data?.detail ?? String(e));
    } finally {
      setSaving(false);
    }
  };

  const updateField = (idx: number, patch: Partial<ParsedInvoicePreview>) => {
    setParsed((p) => p.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-end z-50" onClick={onClose}>
      <div
        className="bg-white w-full max-w-6xl h-full overflow-auto p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4 border-b pb-2">
          <h2 className="text-lg font-semibold">📄 Upload PDF hoá đơn tự động</h2>
          <button onClick={onClose} className="text-gray-500 text-xl hover:text-gray-900">
            ✕
          </button>
        </div>

        <div className="text-sm text-gray-600 mb-3">
          Upload nhiều PDF cùng lúc → tool tự đọc → tự phân loại → preview/sửa → lưu hàng
          loạt. Hỗ trợ: HĐ GTGT VAT, HĐ bán hàng HKD, Meta Ads, Google Ads, ViettelPost.
        </div>

        {/* Upload zone — 2 modes: chọn file lẻ HOẶC chọn cả folder */}
        <div className="border-2 border-dashed border-gray-300 rounded p-4 bg-gray-50 mb-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <div className="text-xs font-medium text-gray-700 mb-1">
                📄 Chọn file PDF
              </div>
              <input
                type="file"
                accept=".pdf,application/pdf"
                multiple
                onChange={(e) => {
                  handleFiles(e.target.files);
                  e.currentTarget.value = "";
                }}
                className="block w-full text-xs cursor-pointer"
              />
              <div className="text-[10px] text-gray-500 mt-1">
                Chọn nhiều PDF cùng lúc (Ctrl/Shift-click).
              </div>
            </label>

            <label className="block">
              <div className="text-xs font-medium text-gray-700 mb-1">
                📁 Chọn cả thư mục
              </div>
              <input
                type="file"
                /* webkitdirectory: browser tự gửi mọi file con, đệ quy. */
                /* @ts-expect-error — non-standard React attr nhưng được hỗ trợ */
                webkitdirectory=""
                directory=""
                multiple
                onChange={(e) => {
                  handleFiles(e.target.files);
                  e.currentTarget.value = "";
                }}
                className="block w-full text-xs cursor-pointer"
              />
              <div className="text-[10px] text-gray-500 mt-1">
                Quét tất cả PDF trong folder + sub-folder (vd: HOÁ ĐƠN/ĐỆM NGỒI/...).
              </div>
            </label>
          </div>

          {files.length > 0 && (
            <div className="mt-3 pt-2 border-t text-xs text-gray-700">
              <div className="font-medium mb-1 flex items-center gap-2">
                <span>Đã chọn {files.length} PDF</span>
                <button
                  onClick={() => setFiles([])}
                  className="text-red-600 underline text-[10px]"
                >
                  Xoá hết
                </button>
              </div>
              <div className="text-[10px] text-gray-500 max-h-20 overflow-y-auto">
                {files.map((f) => f.name).join(" • ")}
              </div>
            </div>
          )}
        </div>

        {/* Parse button */}
        {files.length > 0 && parsed.length === 0 && (
          <button
            onClick={parseAll}
            disabled={parsing}
            className="w-full px-4 py-2 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50 mb-3"
          >
            {parsing ? "⏳ Đang phân tích..." : `🔍 Phân tích ${files.length} PDF`}
          </button>
        )}

        {errorMsg && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded px-3 py-2 text-sm mb-3 whitespace-pre-wrap">
            {errorMsg}
          </div>
        )}

        {/* Parsed results table */}
        {parsed.length > 0 && (
          <>
            <div className="text-sm font-medium mb-2 flex items-center gap-3 flex-wrap">
              <span>
                Kết quả: <b>{parsed.filter((p) => p.success).length}</b>/{parsed.length} parse OK
              </span>
              {parsed.filter((p) => p.duplicate_of).length > 0 && (
                <span className="text-orange-700 bg-orange-50 px-2 py-0.5 rounded">
                  ⚠️ {parsed.filter((p) => p.duplicate_of).length} HĐ trùng (sẽ bỏ qua khi lưu)
                </span>
              )}
              {parsed.filter(
                (p) =>
                  p.success &&
                  p.invoice_no &&
                  !p.duplicate_of &&
                  !p.supplier_match &&
                  !(p.seller_name && p.seller_name.trim())
              ).length > 0 && (
                <span className="text-red-700 bg-red-50 px-2 py-0.5 rounded">
                  ⚠️{" "}
                  {
                    parsed.filter(
                      (p) =>
                        p.success &&
                        p.invoice_no &&
                        !p.duplicate_of &&
                        !p.supplier_match &&
                        !(p.seller_name && p.seller_name.trim())
                    ).length
                  }{" "}
                  thiếu tên NCC (cần điền tay)
                </span>
              )}
              {parsed.filter((p) => p.success && p.total === 0).length > 0 && (
                <span className="text-red-700 bg-red-50 px-2 py-0.5 rounded">
                  ⚠️ {parsed.filter((p) => p.success && p.total === 0).length}{" "}
                  thiếu tổng tiền (parser miss — cần điền tay)
                </span>
              )}
              <span className="text-green-700 bg-green-50 px-2 py-0.5 rounded">
                Sẽ lưu: <b>{parsed.filter(isSavable).length}</b>
              </span>
            </div>
            <div className="overflow-x-auto mb-3">
              <table className="w-full text-xs border">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border px-2 py-1 text-left">File</th>
                    <th className="border px-2 py-1 text-left">NCC (MST)</th>
                    <th className="border px-2 py-1 text-left">Số HĐ</th>
                    <th className="border px-2 py-1 text-left">Ngày</th>
                    <th className="border px-2 py-1 text-right">Tiền hàng</th>
                    <th className="border px-2 py-1 text-right">VAT</th>
                    <th className="border px-2 py-1 text-right">Tổng</th>
                    <th className="border px-2 py-1 text-left">Loại</th>
                    <th className="border px-2 py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.map((p, i) => {
                    const missingNcc =
                      p.success &&
                      !p.supplier_match &&
                      !(p.seller_name && p.seller_name.trim());
                    const willSkip = !isSavable(p);
                    return (
                    <tr
                      key={i}
                      className={
                        !p.success
                          ? "border-t bg-red-50"
                          : p.duplicate_of
                          ? "border-t bg-gray-100 opacity-60"
                          : missingNcc
                          ? "border-t bg-red-50/60"
                          : "border-t hover:bg-gray-50"
                      }
                      title={
                        willSkip
                          ? "Sẽ bỏ qua khi lưu (trùng / thiếu NCC / parse fail)"
                          : ""
                      }
                    >
                      <td className="border px-2 py-1 truncate max-w-[10rem]" title={p.filename}>
                        {p.success ? "✓" : "✗"} {p.filename}
                      </td>
                      {p.success ? (
                        <>
                          <td className="border px-2 py-1">
                            <input
                              value={p.seller_name || ""}
                              onChange={(e) =>
                                updateField(i, { seller_name: e.target.value })
                              }
                              placeholder={missingNcc ? "⚠️ Nhập tên NCC tay…" : "Tên NCC"}
                              className={`w-full border rounded px-1 py-0.5 text-xs ${
                                missingNcc ? "border-red-400 bg-red-50" : ""
                              }`}
                            />
                            <div className="mt-0.5 flex items-center gap-1 flex-wrap">
                              {p.duplicate_of && (
                                <span
                                  className="px-1 py-0.5 text-[10px] font-medium text-orange-700 bg-orange-100 rounded"
                                  title={`Đã có HĐ id=${p.duplicate_of.id}, tổng ${p.duplicate_of.total.toLocaleString()}đ`}
                                >
                                  ⚠️ TRÙNG ({p.duplicate_of.date ?? "?"})
                                </span>
                              )}
                              {missingNcc && !p.duplicate_of && (
                                <span className="px-1 py-0.5 text-[10px] font-medium text-red-700 bg-red-100 rounded">
                                  ⚠️ Thiếu tên NCC
                                </span>
                              )}
                            </div>
                            <div className="text-[10px] text-gray-500 mt-0.5">
                              MST:{" "}
                              <input
                                value={p.seller_tax_code || ""}
                                onChange={(e) =>
                                  updateField(i, { seller_tax_code: e.target.value })
                                }
                                placeholder="MST"
                                className="w-28 border-b border-dotted px-0.5 text-[10px] bg-transparent"
                              />
                              {p.supplier_match && (
                                <span className="text-green-600 ml-1">
                                  ✓ đã có NCC
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="border px-2 py-1 font-mono text-[10px]">
                            <input
                              value={p.invoice_no || ""}
                              onChange={(e) =>
                                updateField(i, { invoice_no: e.target.value })
                              }
                              placeholder="Số HĐ"
                              className="w-24 border rounded px-1 py-0.5 text-xs"
                            />
                          </td>
                          <td className="border px-2 py-1">
                            <input
                              type="date"
                              value={p.invoice_date || ""}
                              onChange={(e) =>
                                updateField(i, { invoice_date: e.target.value })
                              }
                              className="border rounded px-1 py-0.5 text-xs"
                            />
                          </td>
                          <td className="border px-2 py-1 text-right">
                            <input
                              type="number"
                              value={p.amount_before_tax || ""}
                              onChange={(e) =>
                                updateField(i, {
                                  amount_before_tax: parseFloat(e.target.value) || 0,
                                })
                              }
                              placeholder="0"
                              className="w-24 border rounded px-1 py-0.5 text-xs text-right"
                            />
                          </td>
                          <td className="border px-2 py-1 text-right">
                            <input
                              type="number"
                              value={p.vat_amount || ""}
                              onChange={(e) =>
                                updateField(i, {
                                  vat_amount: parseFloat(e.target.value) || 0,
                                })
                              }
                              placeholder="0"
                              className="w-20 border rounded px-1 py-0.5 text-xs text-right"
                            />
                            <span className="text-[10px] text-gray-500 ml-0.5">
                              {(p.vat_rate * 100).toFixed(0)}%
                            </span>
                          </td>
                          <td className="border px-2 py-1 text-right">
                            <input
                              type="number"
                              value={p.total || ""}
                              onChange={(e) =>
                                updateField(i, {
                                  total: parseFloat(e.target.value) || 0,
                                })
                              }
                              placeholder="⚠️ 0"
                              className={`w-24 border rounded px-1 py-0.5 text-xs text-right font-semibold ${
                                p.total === 0 ? "border-red-400 bg-red-50" : ""
                              }`}
                            />
                          </td>
                          <td className="border px-2 py-1">
                            <select
                              value={p.category || ""}
                              onChange={(e) =>
                                updateField(i, { category: e.target.value })
                              }
                              className="border rounded px-1 py-0.5 text-xs w-full"
                            >
                              <option value="">— Chọn —</option>
                              <optgroup label="Vật tư">
                                <option value="Đệm ngồi">Đệm ngồi</option>
                                <option value="Hộp carton">Hộp carton</option>
                                <option value="Miếng dán">Miếng dán</option>
                                <option value="Túi gói hàng">Túi gói hàng</option>
                                <option value="Tem nhãn">Tem nhãn</option>
                                <option value="Mua hàng">Mua hàng (khác)</option>
                              </optgroup>
                              <optgroup label="Dịch vụ / chi phí">
                                <option value="Quảng cáo">Quảng cáo</option>
                                <option value="Phí vận chuyển">Phí vận chuyển</option>
                                <option value="Phí POScake">Phí POScake</option>
                                <option value="Dịch vụ viễn thông">Dịch vụ viễn thông</option>
                                <option value="Khác">Khác</option>
                              </optgroup>
                              {p.category &&
                                ![
                                  "Đệm ngồi", "Hộp carton", "Miếng dán",
                                  "Túi gói hàng", "Tem nhãn", "Mua hàng",
                                  "Quảng cáo", "Phí vận chuyển", "Phí POScake",
                                  "Dịch vụ viễn thông", "Khác",
                                ].includes(p.category) && (
                                  <option value={p.category}>{p.category}</option>
                                )}
                            </select>
                            {p.folder_hint && (
                              <div className="text-[9px] text-gray-500 mt-0.5">
                                📁 {p.folder_hint}
                              </div>
                            )}
                          </td>
                          <td className="border px-2 py-1">
                            <button
                              onClick={() => setEditIdx(i)}
                              className="text-blue-600 hover:underline text-[10px]"
                            >
                              Items ({p.items.length})
                            </button>
                          </td>
                        </>
                      ) : (
                        <td colSpan={8} className="border px-2 py-1 text-red-700">
                          ✗ {p.error}
                        </td>
                      )}
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            </div>

            <div className="flex gap-2 pt-3 border-t sticky bottom-0 bg-white">
              <button
                onClick={onClose}
                className="px-4 py-2 border rounded text-sm hover:bg-gray-50 flex-1"
              >
                Huỷ
              </button>
              <button
                onClick={saveAll}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 flex-1"
              >
                {saving
                  ? "💾 Đang lưu..."
                  : `💾 Lưu ${parsed.filter(isSavable).length} hoá đơn`}
              </button>
            </div>
          </>
        )}

        {/* Item details edit modal */}
        {editIdx !== null && parsed[editIdx] && (
          <div
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
            onClick={() => setEditIdx(null)}
          >
            <div
              className="bg-white max-w-3xl w-full max-h-[80vh] overflow-auto p-5 rounded"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold mb-3">
                Chi tiết hàng: {parsed[editIdx].filename}
              </h3>
              <table className="w-full text-xs border">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border px-2 py-1 text-left">Tên hàng</th>
                    <th className="border px-2 py-1">ĐVT</th>
                    <th className="border px-2 py-1 text-right">SL</th>
                    <th className="border px-2 py-1 text-right">Đơn giá</th>
                    <th className="border px-2 py-1 text-right">Thành tiền</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed[editIdx].items.map((it, j) => (
                    <tr key={j} className="border-t">
                      <td className="border px-2 py-1">{it.description}</td>
                      <td className="border px-2 py-1 text-center">{it.unit || "—"}</td>
                      <td className="border px-2 py-1 text-right">{it.quantity}</td>
                      <td className="border px-2 py-1 text-right">{fmtVND(it.unit_price)}</td>
                      <td className="border px-2 py-1 text-right">{fmtVND(it.amount)}</td>
                    </tr>
                  ))}
                  {parsed[editIdx].items.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center text-gray-500 py-4">
                        Không parse được items chi tiết (sẽ lưu chỉ tổng)
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              <button
                onClick={() => setEditIdx(null)}
                className="mt-3 px-4 py-1.5 bg-gray-200 rounded text-sm"
              >
                Đóng
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
